/**
 * CikisNoktasiPinner.jsx — Teknik Talep çıkış noktası işaretleme (kroki + plan
 * yan yana)
 *
 * 2026-08-29'a kadar çıkış noktası pini iki ayrı dosyada birbirinin kopyası olarak
 * duruyordu (FirmaTalepStandDetailPage.jsx'teki InteractiveKrokiPin ve
 * FirmaStandDetailPage.jsx'teki salt-okunur KrokiShape). Proje Beyanı sihirbazına
 * "çıkış noktaları" adımı eklenince üçüncü bir kopya çıkacaktı; bunun yerine tek
 * bileşene taşındı.
 *
 * ⚠️ ASIL DEĞİŞİKLİK: pin artık TEK BAŞINA bir kroki değil. Dekoratör çıkış
 *    noktasını, kendi yüklediği <b>Teknik Talep Planı</b>'na (Kuşbakışı) BAKARAK
 *    işaretler — plan solda, kroki sağda, aynı ekranda. Karşılaştıracak bir çizim
 *    olmadan işaretlenen nokta tahminden ibaret olduğu için:
 *
 *    **Teknik Talep Planı yüklenmemişse pinleme KAPALIDIR.** (`planFile` yoksa
 *    kroki tıklanamaz ve neden kapalı olduğu yazılır.) Aynı kural hem Proje Beyanı
 *    sihirbazının "Çıkış Noktaları" adımında hem Talepler sayfasının "Teknik
 *    Talepler" sekmesinde geçerli.
 *
 * #676 — GERÇEK KROKİ: `kontur` verilirse (Proje Beyanı sihirbazı, standın çizimi FDS-Backend'e itildiyse:
 *    standKroki.cizimKonturu) pin, sabit L şekli yerine standın ÇİZİLMİŞ ana hattı üzerine konur ve değer {x,y}
 *    TÜM şekillerin birlikte SINIR KUTUSUNA göre 0-1'dir (#495'in beklediği budur). `kontur` yoksa (çizilmemiş
 *    stand, okunamayan çizim, mock, Talepler sayfasının Teknik sekmesi) eski L şekli ve eski 0-1 anlamı aynen kalır.
 *
 * Plan önizlemesi üç kaynaktan gelebilir, bu sırayla:
 *   1. `localPreviewUrl` — kullanıcının AZ ÖNCE bu oturumda seçtiği dosyanın
 *      URL.createObjectURL'i. Mock modda backend'de gerçek içerik olmadığı için
 *      tek gerçek önizleme yolu budur (çağıran taraf üretir/serbest bırakır).
 *   2. `planUrl` — canlı modda /public/files/project_files/{storedFileName}
 *      (bkz. projectService.getProjectFileUrl). Sadece GÖRSEL uzantılarda <img>
 *      olarak gömülür.
 *   3. Hiçbiri yoksa dosya adı + "Yeni Sekmede Aç" bağlantısı — PDF'ler bu yola
 *      düşer: backend bu yolu `X-Frame-Options: DENY` ile servis ettiği için
 *      <iframe> ile gömülemiyorlar (bkz. DocumentPreviewModal.js'teki aynı not).
 */
import React, { useId, useRef } from "react";
import { Alert, Badge, Button, Col, Row } from "reactstrap";
import { ExternalLink, FileText, ImageOff, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ekrandanKrokiye, krokidenNormal, normaldenKrokiye } from "./standKroki";

// Kroki poligonu — FirmaStandDetailPage.jsx'teki KrokiShape /
// FirmaProjelerListPage.jsx'teki WallDimensionsKroki ile AYNI sabit L şekli
// (viewBox 0 0 120 120). Şekli değiştiren biri üçünü birden değiştirmeli.
const KROKI_POLYGON = "10,10 65,10 65,65 110,65 110,110 10,110";

function isImageName(name) {
  return /\.(jpe?g|png|gif|webp|svg)$/i.test(name || "");
}

/**
 * Krokiye tıklayarak/pin koyarak çıkış noktası seçme.
 * `value`: {x,y} — 0-1 normalize, krokinin viewBox'ına göre | null.
 * ⚠️ Tıklanan konum SADECE yerel state'e yazılır; kalıcı kayıt çağıran tarafın
 *    "Kaydet" düğmesindedir (kazara tıklama geri alınabilsin diye).
 */
export function InteractiveKrokiPin({ value, onChange, size = 160, disabled = false, fluid = false, kontur = null }) {
  const gridId = useId();
  const svgRef = useRef(null);

  // #676: standın gerçek ana hattı varsa o çizilir; yoksa aşağıdaki eski sabit L şekli.
  if (kontur) {
    return <GercekKrokiPin kontur={kontur} value={value} onChange={onChange} size={size} disabled={disabled} fluid={fluid} />;
  }

  function handleClick(e) {
    if (disabled) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
    onChange({ x, y });
  }

  return (
    <svg
      ref={svgRef} viewBox="0 0 120 120"
      width={fluid ? "100%" : size} height={fluid ? undefined : size}
      style={{
        cursor: disabled ? "not-allowed" : "crosshair",
        opacity: disabled ? 0.45 : 1,
        // fluid: sütunun genişliğini doldur, kareliği aspect-ratio korusun (kart
        // içindeki yarım-sayfa kutularda sabit px kroki taşıyordu). Tıklama
        // getBoundingClientRect ile okunduğu için ölçekten etkilenmiyor.
        ...(fluid ? { width: "100%", maxWidth: size, aspectRatio: "1 / 1" } : null),
      }}
      onClick={handleClick}
    >
      <defs>
        <pattern id={gridId} width="15" height="15" patternUnits="userSpaceOnUse">
          <path d="M 15 0 L 0 0 0 15" fill="none" stroke="#d7dfe3" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="120" height="120" fill={`url(#${gridId})`} />
      <polygon
        points={KROKI_POLYGON}
        fill="#178f8f" fillOpacity="0.18" stroke="#178f8f" strokeWidth="1.5"
      />
      {value && (
        <circle cx={value.x * 120} cy={value.y * 120} r="5" fill="#dc3545" stroke="#fff" strokeWidth="1.5" />
      )}
    </svg>
  );
}

/**
 * #676 — standın ÇİZİLMİŞ ana hattı üzerinde çıkış noktası. `value` / `onChange`: {x,y} 0-1, TÜM şekillerin sınır kutusuna göre
 * (kutunun dışına tıklanırsa kenara çekilir). Tıklama svg'nin kendi yerleşimiyle (xMidYMid meet) kroki koordinatına çevrilir,
 * bu yüzden kare olmayan bir kutuda da pin tıklanan yere düşer.
 */
function GercekKrokiPin({ kontur, value, onChange, size, disabled, fluid }) {
  const { t } = useTranslation("components");
  const svgRef = useRef(null);
  const vb = kontur.viewBox;
  const buyuk = Math.max(vb.w, vb.h);
  const pin = value ? normaldenKrokiye(kontur, value) : null;

  function handleClick(e) {
    if (disabled) return;
    const rect = svgRef.current.getBoundingClientRect();
    onChange(krokidenNormal(kontur, ekrandanKrokiye(rect, vb, e.clientX, e.clientY)));
  }

  return (
    <svg
      ref={svgRef} viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      role="img" aria-label={t("cikisNoktasi.krokiEtiketi")}
      style={{
        cursor: disabled ? "not-allowed" : "crosshair",
        opacity: disabled ? 0.45 : 1,
        background: "#f8f9fa",
        borderRadius: 6,
        // büyük kenar `size`a sığar; yükseklik/genişlik oranı çizimin kendisidir
        ...(fluid
          ? { width: "100%", maxWidth: size, maxHeight: size, aspectRatio: `${vb.w} / ${vb.h}` }
          : { width: (vb.w / buyuk) * size, height: (vb.h / buyuk) * size }),
      }}
      onClick={handleClick}
    >
      {kontur.shapes.map((s) => (
        <polygon
          key={s.shapeId} points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="#178f8f" fillOpacity="0.18" stroke="#178f8f" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round"
        />
      ))}
      {pin && (
        <circle
          cx={pin.x} cy={pin.y} r={buyuk * 0.035} fill="#dc3545" stroke="#fff" strokeWidth="1.5" vectorEffect="non-scaling-stroke"
          data-pin="1"
        />
      )}
    </svg>
  );
}

// Sol yarı — yüklenen Teknik Talep Planı. Gömülemeyen tipler (PDF) için dosya adı
// + yeni sekme bağlantısı; hiç dosya yoksa neyin eksik olduğunu SÖYLEYEN bir kutu
// (boş bir çerçeve "yükleniyor mu, bozuk mu?" sorusunu doğuruyordu).
function PlanOnizleme({ planFile, planUrl, localPreviewUrl, height }) {
  const { t } = useTranslation("components");
  const name = planFile?.originalFileName ?? planFile?.name ?? null;
  const embeddableUrl = localPreviewUrl ?? (planUrl && isImageName(name) ? planUrl : null);
  // `height` verilmezse yükseklik SARMALAYICIDAN gelir ve önizleme kalan alanı
  // doldurur — kart içi (embedded) kullanımda plan kutusu, yanındaki akışkan
  // krokiyle aynı kare ölçüyü tutsun diye böyle (bkz. CikisNoktasiPinner).
  const fillStyle = height != null ? { height } : { flex: "1 1 auto", minHeight: 0 };

  if (!planFile) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-center border rounded-3 bg-light text-muted p-3 h-100"
        style={{ minHeight: height }}
      >
        <ImageOff size={26} className="mb-2" />
        <div className="small fw-semibold">{t("cikisNoktasi.teknikTalepPlaniYok")}</div>
        <div className="small">{t("cikisNoktasi.kusbakisiCiziminiziYuklemedenCikisNoktasi")}</div>
      </div>
    );
  }

  return (
    <div className="border rounded-3 overflow-hidden bg-light h-100 d-flex flex-column">
      {embeddableUrl ? (
        <img
          src={embeddableUrl} alt={name ?? t("cikisNoktasi.teknikTalepPlani")}
          className="w-100"
          style={{ ...fillStyle, objectFit: "contain", background: "#fff" }}
        />
      ) : (
        <div
          className="d-flex flex-column align-items-center justify-content-center text-center text-muted p-3"
          style={fillStyle}
        >
          <FileText size={26} className="mb-2" />
          <div className="small text-truncate w-100">{name}</div>
          <div className="small">{t("cikisNoktasi.buDosyaTipiBuradaGomulu")}</div>
          {planUrl && (
            <Button color="secondary" outline size="sm" className="mt-2" tag="a" href={planUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={13} className="me-1" />{t("cikisNoktasi.yeniSekmedeAc")}
            </Button>
          )}
        </div>
      )}
      <div className="px-2 py-1 border-top small text-muted text-truncate bg-white">{name}</div>
    </div>
  );
}

/**
 * Bir Teknik Talep kategorisi için "planına bakarak krokide işaretle" bloğu.
 *
 * İki yerleşimi var:
 *   - varsayılan (Proje Beyanı sihirbazının "Çıkış Noktaları" adımı): tam genişlikte,
 *     kendi çerçevesi + ikon/başlık satırıyla, kategoriler alt alta listelenir.
 *   - `embedded` (Talepler → Teknik Talepler kartlarının içi, 2026-09-04): kategori
 *     kartı ZATEN ikonu, adını ve switch'i taşıdığı için çerçeve/başlık tekrar
 *     edilmez — kartın alt yarısına sadece "plan | kroki" ikilisi düşer ve ikisi de
 *     sütun genişliğine göre akışkandır (yarım-sayfa kutuda sabit px taşıyordu).
 *
 * @param {object}   category         TEKNIK_TALEP_KATEGORILERI satırı ({key,label,color})
 * @param {Function} icon             lucide ikonu (çağıran tarafın TEKNIK_ICONS eşlemesinden)
 * @param {object}   planFile         Yüklenmiş Teknik Talep Planı kaydı ya da yerel File — yoksa pin kapalı
 * @param {string}   planUrl          Canlı dosya URL'i (projectService.getProjectFileUrl)
 * @param {string}   localPreviewUrl  Bu oturumda seçilen dosyanın objectURL'i (mock modda tek önizleme yolu)
 * @param {object}   kontur           #676: standın çizilmiş ana hattı (standKroki.cizimKonturu) | null = eski sabit L şekli
 * @param {object}   value            {x,y} | null
 * @param {Function} onChange         (point) => void
 * @param {boolean}  embedded         Kategori kartının içine gömülü, akışkan yerleşim
 */
export default function CikisNoktasiPinner({
  category, icon: Icon, planFile, planUrl, localPreviewUrl,
  value, onChange, embedded = false, kontur = null,
  // embedded'da krokiSize bir SABİT ölçü değil ÜST SINIR: iki kare de kendi
  // sütununu doldurur, sadece çok geniş ekranda bu değerde durur. Plan kutusu ve
  // kroki aynı sınırı paylaşır — yoksa biri sütunu doldurup diğeri 170'te kalıyor
  // ve ikili yamuk görünüyordu.
  krokiSize = embedded ? 280 : 170, planHeight = 170,
}) {
  const { t } = useTranslation("components");
  const pinDisabled = !planFile;

  const durumRozeti = value
    ? <Badge color="success"><MapPin size={11} className="me-1" />{t("cikisNoktasi.isaretlendi")}</Badge>
    : <Badge color="light" className="text-dark">{t("cikisNoktasi.isaretlenmedi")}</Badge>;

  const planSutunu = (
    <>
      <div className="text-muted small mb-1 text-truncate">
        {embedded ? t("cikisNoktasi.teknikTalepPlani") : t("cikisNoktasi.gonderdiginizTeknikTalepPlani")}
      </div>
      {/* embedded: kare kutu — yükseklik buradan gelir, PlanOnizleme kalanı doldurur. */}
      <div
        className={embedded ? "d-flex flex-column" : undefined}
        style={embedded ? { aspectRatio: "1 / 1", maxWidth: krokiSize } : undefined}
      >
        <PlanOnizleme
          planFile={planFile} planUrl={planUrl} localPreviewUrl={localPreviewUrl}
          height={embedded ? undefined : planHeight}
        />
      </div>
    </>
  );

  const krokiSutunu = (
    <>
      <div className="text-muted small mb-1 text-truncate">{t("cikisNoktasi.krokiTiklayarakIsaretleyin")}</div>
      {/* ⚠️ <svg> satır-içi bir öğe: sarmalanmazsa "İşareti kaldır" düğmesi krokinin
          YANINA geçip sütunu genişletiyor ve blok bir alt satıra sarıyor. */}
      <div>
        <InteractiveKrokiPin
          value={value} onChange={onChange}
          size={krokiSize} fluid={embedded} disabled={pinDisabled} kontur={kontur}
        />
      </div>
      {kontur && <div className="text-muted small">{t("cikisNoktasi.cizimdenKroki")}</div>}
      {/* #710: no outline = the fixed L, which is a SAMPLE, not this stand's shape: said so, so the pin is not read as a position on the stand */}
      {!kontur && <div className="text-muted small">{t("cikisNoktasi.ornekSekilKroki")}</div>}
      {value && !pinDisabled && (
        <Button color="link" size="sm" className="p-0" onClick={() => onChange(null)}>
          {t("cikisNoktasi.isaretiKaldir")}
        </Button>
      )}
    </>
  );

  const aciklama = pinDisabled ? (
    <Alert color="warning" className="small mb-0 mt-2">
      {t("cikisNoktasi.buNoktayiIsaretleyebilmekIcinOnce")}{" "}<span className="fw-semibold">{t("cikisNoktasi.kusbakisiTeknikTalepPlani")}</span>{" "}
      {t("cikisNoktasi.dosyaniziYukleyinCikisNoktasiGonderdiginiz")}
    </Alert>
  ) : (
    <div className="text-muted small mt-2">
      {t("cikisNoktasi.soldakiCiziminizdeCikisininNereyeGeldigine", { v: category?.label?.toLowerCase() })}
    </div>
  );

  if (embedded) {
    return (
      <div className="border-top pt-2 mt-2">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="small fw-semibold">{t("cikisNoktasi.cikisNoktasi")}</span>
          {durumRozeti}
        </div>
        <Row className="g-2">
          <Col xs={6}>{planSutunu}</Col>
          <Col xs={6} className="text-center">{krokiSutunu}</Col>
        </Row>
        {aciklama}
      </div>
    );
  }

  return (
    <div className="border rounded-3 p-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        {Icon && (
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 30, height: 30, background: category?.color ?? "#878a99" }}
          >
            <Icon size={15} color="#fff" />
          </div>
        )}
        <span className="fw-semibold">{category?.label}{" "}{t("cikisNoktasi.cikisNoktasi2")}</span>
        <span className="ms-auto">{durumRozeti}</span>
      </div>

      <div className="d-flex flex-wrap gap-3 align-items-start">
        <div style={{ flex: "1 1 220px", minWidth: 200 }}>{planSutunu}</div>
        <div className="text-center" style={{ flex: "0 0 auto", width: krokiSize }}>{krokiSutunu}</div>
      </div>

      {aciklama}
    </div>
  );
}
